import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDateInput, getTodayDateString } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";
import type { Goal } from "@shared/schema";

interface GoalProgressEntriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: Goal;
}

interface ProgressEntry {
  id: string;
  goalId: string;
  tenantId: string;
  userId: string;
  date: string | Date;
  amount: number;
  notes: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
}

const entryFormSchema = z.object({
  date: z.string().min(1, "Date is required"),
  amount: z.number().min(0, "Amount must be positive"),
  notes: z.string().optional(),
});

type EntryFormData = z.infer<typeof entryFormSchema>;

export default function GoalProgressEntriesDialog({
  open,
  onOpenChange,
  goal,
}: GoalProgressEntriesDialogProps) {
  const { toast } = useToast();
  const [editingEntry, setEditingEntry] = useState<ProgressEntry | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: entries = [], isLoading } = useQuery<ProgressEntry[]>({
    queryKey: ["/api/goals", goal.id, "progress-entries"],
    enabled: open,
  });

  const form = useForm<EntryFormData>({
    resolver: zodResolver(entryFormSchema),
    defaultValues: {
      date: getTodayDateString(),
      amount: 0,
      notes: "",
    },
  });

  const createEntryMutation = useMutation({
    mutationFn: (data: EntryFormData) =>
      apiRequest("POST", `/api/goals/${goal.id}/progress-entries`, {
        ...data,
        date: new Date(data.date),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      form.reset();
      setShowForm(false);
    },
    onError: () => {
      // Keep the form open with the user's values so they can retry.
      toast({ title: "Couldn't add progress entry", description: "Your entry was not saved. Please try again.", variant: "destructive" });
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({ id, data, initialDate }: { id: string; data: EntryFormData; initialDate: string }) => {
      // Only send `date` when the user actually changed it. The form shows the
      // entry's date in the browser's local timezone, so re-sending it
      // unchanged could shift entries near UTC midnight to a different UTC
      // day. When the date IS changed, send the date-only string as-is; the
      // server preserves the entry's original time-of-day on the new day.
      const payload: Partial<EntryFormData> = { amount: data.amount, notes: data.notes };
      if (data.date !== initialDate) payload.date = data.date;
      return apiRequest("PATCH", `/api/goals/${goal.id}/progress-entries/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      form.reset();
      setEditingEntry(null);
      setShowForm(false);
    },
    onError: () => {
      // Keep the form open with the user's values so they can retry.
      toast({ title: "Couldn't save progress entry", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/goals/${goal.id}/progress-entries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "progress-entries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      toast({ title: "Couldn't delete progress entry", description: "Please try again.", variant: "destructive" });
    },
  });

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createEntryMutation.isPending) submitLockRef.current = false;
  }, [createEntryMutation.isPending]);

  const onSubmit = (data: EntryFormData) => {
    if (editingEntry) {
      updateEntryMutation.mutate({
        id: editingEntry.id,
        data,
        initialDate: formatDateInput(new Date(editingEntry.date)),
      });
    } else {
      if (submitLockRef.current || createEntryMutation.isPending) return;
      submitLockRef.current = true;
      createEntryMutation.mutate(data);
    }
  };

  const handleEdit = (entry: ProgressEntry) => {
    setEditingEntry(entry);
    form.reset({
      date: formatDateInput(new Date(entry.date)),
      amount: entry.amount,
      notes: entry.notes || "",
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingEntry(null);
    setShowForm(false);
    form.reset();
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number) => {
    if (goal.customGoalType === "currency") {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const totalProgress = entries.reduce((sum, entry) => sum + entry.amount, 0);
  const progressPercentage = goal.customGoalTarget
    ? Math.min(100, Math.round((totalProgress / goal.customGoalTarget) * 100))
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-progress-entries">
        <DialogHeader>
          <DialogTitle>
            Track Progress: {goal.customGoalTitle || "Custom Goal"}
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            Target: {formatAmount(goal.customGoalTarget || 0)} • Current: {formatAmount(totalProgress)} • Progress: {progressPercentage}%
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full"
              data-testid="button-add-progress-entry"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Progress Entry
            </Button>
          )}

          {showForm && (
            <div className="border border-border rounded-lg p-4 space-y-4">
              <h3 className="font-semibold">
                {editingEntry ? "Edit Entry" : "New Entry"}
              </h3>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            data-testid="input-entry-date"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Amount {goal.customGoalType === "currency" && "($)"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter amount"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                            data-testid="input-entry-amount"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Notes (Optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Add any notes about this entry..."
                            rows={2}
                            {...field}
                            value={field.value || ""}
                            data-testid="textarea-entry-notes"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      data-testid="button-cancel-entry"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitLockRef.current || createEntryMutation.isPending || updateEntryMutation.isPending}
                      data-testid="button-save-entry"
                    >
                      {createEntryMutation.isPending || updateEntryMutation.isPending
                        ? "Saving..."
                        : editingEntry
                        ? "Update Entry"
                        : "Add Entry"}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-semibold">Progress History</h3>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                Loading entries...
              </div>
            ) : entries.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No progress entries yet. Add your first entry above!
              </div>
            ) : (
              <div className="space-y-2">
                {entries
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between p-3 border border-border rounded-md hover:bg-muted/50"
                      data-testid={`entry-item-${entry.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{formatAmount(entry.amount)}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(entry.date)}
                          </span>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(entry)}
                          data-testid={`button-edit-entry-${entry.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => deleteEntryMutation.mutate(entry.id)}
                          disabled={deleteEntryMutation.isPending}
                          data-testid={`button-delete-entry-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-close-dialog"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
